// Shared API cache and request management
import axios from 'axios';

// Global cache for API responses
const apiCache = new Map();
const pendingRequests = new Map();

// Cache TTL in milliseconds (5 minutes)
const CACHE_TTL = 5 * 60 * 1000;

// Get cache key for URL and parameters
const getCacheKey = (url, params = {}) => {
  return `${url}?${JSON.stringify(params)}`;
};

// Check if cache entry is still valid
const isCacheValid = (timestamp) => {
  return Date.now() - timestamp < CACHE_TTL;
};

// Enhanced fetch with caching and deduplication
export const fetchWithCache = async (url, options = {}) => {
  const cacheKey = getCacheKey(url, options.params);
  const cached = apiCache.get(cacheKey);
  
  // Return cached data if still valid
  if (cached && isCacheValid(cached.timestamp)) {
    console.log(`📦 Cache hit for ${cacheKey}`);
    return cached.data;
  }
  
  // If request is already pending, wait for it
  if (pendingRequests.has(cacheKey)) {
    console.log(`⏳ Waiting for pending request ${cacheKey}`);
    return pendingRequests.get(cacheKey);
  }
  
  // Create new request
  const requestPromise = (async () => {
    try {
      console.log(`🌐 Making new request ${cacheKey}`);
      const response = await axios.get(url, options);
      
      // Cache the response
      apiCache.set(cacheKey, {
        data: response,
        timestamp: Date.now()
      });
      
      return response;
    } catch (error) {
      // Don't cache errors
      apiCache.delete(cacheKey);
      throw error;
    } finally {
      // Remove from pending requests
      pendingRequests.delete(cacheKey);
    }
  })();
  
  pendingRequests.set(cacheKey, requestPromise);
  return requestPromise;
};

// Clear cache for specific URL or all cache
export const clearCache = (url = null) => {
  if (url) {
    const cacheKey = getCacheKey(url);
    apiCache.delete(cacheKey);
    pendingRequests.delete(cacheKey);
  } else {
    apiCache.clear();
    pendingRequests.clear();
  }
};

// Get cache statistics
export const getCacheStats = () => {
  return {
    cacheSize: apiCache.size,
    pendingRequests: pendingRequests.size,
    cacheEntries: Array.from(apiCache.keys())
  };
};

// Retry mechanism with exponential backoff and cache support
export const fetchWithRetryAndCache = async (url, maxRetries = 3, options = {}) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const response = await fetchWithCache(url, options);
      return response;
    } catch (error) {
      // Don't retry if request was cancelled
      if (axios.isCancel(error)) {
        throw error;
      }
      
      // Don't retry on client errors (4xx)
      if (error.response && error.response.status >= 400 && error.response.status < 500) {
        throw error;
      }
      
      // Last retry - throw the error
      if (i === maxRetries - 1) {
        console.error(`Max retries (${maxRetries}) reached for ${url}`);
        throw error;
      }
      
      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, i) * 1000;
      console.log(`Retry ${i + 1}/${maxRetries} failed for ${url}, waiting ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};
